import { isRecord } from "@/utils/isRecord";
import { useSpotUser } from "@/utils/spotUser";
import { DATA_KEYS, useRemoteData } from "@/utils/useRemoteData";
import type { ToolPartBase } from "../ToolComponents";
import { isActionablePendingApproval } from "./approvalGate";
import type { ApprovalView } from "./approvalView";
import { EntityApprovalBody, InlineRow } from "./EntityApprovalBody";
import { describeInputs, describeSchedule } from "./triggerSummary";

// The labels of a flow's inputs by node id, as its editor shows them.
function inputLabels(workflow: unknown): Map<string, string> {
  const definition = isRecord(workflow) ? workflow.definition : undefined;
  const nodes = isRecord(definition) ? definition.nodes : undefined;
  const labels = new Map<string, string>();
  for (const node of Array.isArray(nodes) ? nodes : []) {
    if (!isRecord(node) || node.type !== "InputNodeV2") continue;
    const config = isRecord(node.node_config) ? node.node_config : {};
    const label = config.label ?? node.name;
    if (typeof node.id === "string" && typeof label === "string")
      labels.set(node.id, label);
  }
  return labels;
}

// A new trigger in words: when it runs, in the viewer's time and in UTC, and
// what each flow input gets, by the input's label.
export function NewTriggerBody({
  view,
  part,
}: {
  view: ApprovalView;
  part: ToolPartBase;
}) {
  const { spotGroup } = useSpotUser();
  const input = isRecord(part.input) ? part.input : {};
  const definition = isRecord(input.definition) ? input.definition : {};
  const workflowId =
    typeof input.workflow_id === "string" ? input.workflow_id : "";
  const workflow = useRemoteData(
    DATA_KEYS.WORKFLOWS_ID,
    { path: { group_id: spotGroup.id, workflow_id: workflowId } },
    { enabled: !!spotGroup.id && !!workflowId, retry: false }
  ).data;
  // A trigger runs the flow's latest saved version, bound when it is made.
  const latestVersion = useRemoteData(
    DATA_KEYS.WORKFLOW_VERSIONS,
    {
      path: { group_id: spotGroup.id, workflow_id: workflowId },
      query: { page: 1, size: 1 },
    },
    {
      enabled:
        !!spotGroup.id && !!workflowId && isActionablePendingApproval(part),
      retry: false,
    }
  ).data?.items[0];
  const when = describeSchedule(
    definition,
    Intl.DateTimeFormat().resolvedOptions().timeZone
  );
  const inputs = describeInputs(definition, inputLabels(workflow));
  return (
    <EntityApprovalBody view={view} input={part.input}>
      {when ? (
        <InlineRow label="When" edit={false}>
          {when}
        </InlineRow>
      ) : (
        typeof definition.type === "string" && (
          <InlineRow label="Type" edit={false}>
            {definition.type}
          </InlineRow>
        )
      )}
      {latestVersion && (
        <InlineRow label="Runs version" edit={false}>
          {latestVersion.name}
        </InlineRow>
      )}
      {inputs.map((item) => (
        <InlineRow key={item.label} label={item.label} edit={false}>
          {item.value}
          {!item.known && workflow !== undefined && (
            <span className="text-amber-600"> (not an input of this flow)</span>
          )}
        </InlineRow>
      ))}
    </EntityApprovalBody>
  );
}
